import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { userManagementUrl, roleManagemenUrl } from "../../../config";
import { getrolesquery } from "../../../data/Axios/queries";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { fetchSgwSuperusersFromApi } from "../../../data/WalletService/walletGraphQL";

export const WALLET_APPROVAL_TYPES = [
  {
    id: "WITHDRAWAL_APPROVAL",
    label: "Wallet Withdrawal Approval",
    category: "Treasury Payout",
    description: "Checker sign-off for M-Pesa & bank withdrawal payouts from the company wallet.",
    governance: "Maker ≠ Checker (Dual Control + OTP)",
    color: "#0284c7",
    bgLight: "rgba(2, 132, 199, 0.1)",
  },
  {
    id: "OVERDRAFT_FACILITY_APPROVAL",
    label: "Overdraft Facility Approval",
    category: "Credit Facility",
    description: "Authorize overdraft limits, limit expansions, and emergency facility allocations.",
    governance: "Dual Authorization with OTP",
    color: "#7c3aed",
    bgLight: "rgba(124, 58, 237, 0.1)",
  },
];

/**
 * Returns a company-scoped localStorage key for SGW approvals.
 * SGW approvals are platform-wide (not tenant-specific), but the cache is
 * keyed per company so switching companies always triggers a fresh API fetch
 * instead of silently serving a stale cross-company cache.
 */
export const sgwApprovalsStorageKey = (companyId) =>
  `sgw_wallet_approvals_${companyId || "global"}`;

// Legacy static key — kept only for one-time migration cleanup below
export const STORAGE_KEY_SGW_APPROVALS = "sgw_wallet_approvals";

// All hardcoded initial records removed; table initial state is completely live / empty
export const DEFAULT_INITIAL_APPROVALS = [];

/**
 * Fetch and filter only users who belong to Role Group ID 1 (Superusers).
 * Queries live GraphQL endpoint first, falls back to User Management & Role Service.
 * No hardcoded/mock user records.
 */
export async function loadSuperusersOnly() {
  let superuserList = [];

  // 1. Attempt to fetch directly from Wallet GraphQL SGW Superusers query
  try {
    const liveGqlUsers = await fetchSgwSuperusersFromApi();
    if (Array.isArray(liveGqlUsers) && liveGqlUsers.length > 0) {
      return liveGqlUsers.map((u) => ({
        id: Number(u.id),
        name: u.name || u.username || `Superuser #${u.id}`,
        username: u.username || "",
        email: u.email || "—",
        phone: u.phone || "—",
        roleId: Number(u.roleId || 1),
        roleName: u.roleName || "Super Administrator",
        roleGroupId: 1,
      }));
    }
  } catch (gqlErr) {
    // Graceful fallback to User Management & Role Service
  }

  // 2. Read authenticated token user
  let tokenUser = null;
  try {
    const raw = localStorage.getItem("decodedToken");
    if (raw) {
      const d = JSON.parse(raw);
      const uid = Number(d?.Id ?? d?.id ?? d?.UserId ?? d?.userId ?? 0);
      const first = (d?.FirstName ?? d?.firstName ?? "").trim();
      const last = (d?.LastName ?? d?.lastName ?? "").trim();
      const full = `${first} ${last}`.trim();
      const name = full || d?.Username || d?.username || d?.Name || "";
      const email = d?.Email || d?.email || "";
      const phone = d?.Phone || d?.phone || d?.PhoneNumber || "";
      const roleId = Number(d?.RoleId ?? d?.roleId ?? 0);
      const roleGroupId = Number(d?.RoleGroupId ?? d?.roleGroupId ?? 0);
      const roleName = d?.Role || d?.role || "";

      if (uid > 0 && (roleGroupId === 1 || roleName.toLowerCase().includes("super"))) {
        tokenUser = {
          id: uid,
          name: name || `Superuser #${uid}`,
          username: d?.Username || d?.username || `user${uid}`,
          email: email || "—",
          phone: phone || "—",
          roleId: roleId || 1,
          roleGroupId: 1,
          roleName: roleName || "Super Administrator",
        };
      }
    }
  } catch (err) {
    console.warn("Could not parse decodedToken:", err);
  }

  // 3. Query Role Service and User Management for Role Group ID 1
  try {
    const superRoleIds = new Set([1]);
    try {
      if (roleManagemenUrl?.uri) {
        const roles = await graphqlQuery(getrolesquery, roleManagemenUrl.uri);
        if (Array.isArray(roles)) {
          roles.forEach((r) => {
            const rgid = Number(r.roleGroupId ?? r.RoleGroupId);
            const rName = (r.role || r.Role || r.roleName || "").toLowerCase();
            const rgName = (r.roleGroup || r.RoleGroup || "").toLowerCase();
            if (
              rgid === 1 ||
              rName.includes("super") ||
              rgName.includes("super") ||
              rName.includes("administrator")
            ) {
              superRoleIds.add(Number(r.id || r.Id));
            }
          });
        }
      }
    } catch (rErr) {
      console.warn("Could not fetch roles from role service:", rErr);
    }

    if (userManagementUrl?.uri) {
      const res = await fetchJsonDynamic("allUsersView", null, null, userManagementUrl.uri);
      const raw = res?.results;
      const allUsers = Array.isArray(raw)
        ? raw
        : typeof raw === "string"
        ? JSON.parse(raw)
        : [];

      if (Array.isArray(allUsers) && allUsers.length > 0) {
        superuserList = allUsers
          .filter((u) => {
            const roleId = Number(u.RoleId ?? u.roleId);
            const roleGroupId = Number(u.RoleGroupId ?? u.roleGroupId);
            const roleName = (u.Role ?? u.role ?? "").toLowerCase();
            const isSuper =
              roleGroupId === 1 ||
              superRoleIds.has(roleId) ||
              roleName.includes("super") ||
              roleName.includes("administrator");
            return isSuper && u.IsActive !== 0 && u.IsActive !== false;
          })
          .map((u) => {
            const id = Number(u.Id ?? u.id);
            const first = (u.FirstName ?? u.firstName ?? "").trim();
            const last = (u.LastName ?? u.lastName ?? "").trim();
            const full = `${first} ${last}`.trim();
            const name = full || (u.Name ?? u.name ?? "").trim() || (u.Username ?? u.username ?? "").trim() || `Superuser #${id}`;
            return {
              id,
              name,
              username: u.Username ?? u.username ?? `user${id}`,
              email: u.Email ?? u.email ?? "—",
              phone: u.Phone ?? u.phone ?? "—",
              roleId: Number(u.RoleId ?? u.roleId ?? 1),
              roleName: u.Role ?? u.role ?? "Super Administrator",
              roleGroupId: 1, // Strictly Superuser
            };
          });
      }
    }
  } catch (uErr) {
    console.warn("Could not fetch users from user service:", uErr);
  }

  // Include current authenticated user if verified as Role Group ID 1
  if (tokenUser && !superuserList.some((u) => u.id === tokenUser.id)) {
    superuserList.unshift(tokenUser);
  }

  return superuserList;
}

/**
 * Get cached SGW Approvals from localStorage for the given company.
 * Pass the current companyId so each company has its own isolated cache slot.
 */
export function getStoredSgwApprovals(companyId) {
  try {
    const raw = localStorage.getItem(sgwApprovalsStorageKey(companyId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        // Strip any legacy mock seed records that were previously cached in localStorage
        const clean = parsed.filter(
          (item) =>
            item &&
            !String(item.id).startsWith("SGW-APP-100") &&
            item.name !== "Alex Kiprop Mutai" &&
            item.name !== "Grace Wanjiku Kamau" &&
            item.name !== "David Otieno Omondi" &&
            item.name !== "Brian Kiprono Koech"
        );
        if (clean.length !== parsed.length) {
          localStorage.setItem(sgwApprovalsStorageKey(companyId), JSON.stringify(clean));
        }
        return clean;
      }
    }
  } catch (e) {
    console.warn("Error reading stored SGW approvals:", e);
  }
  return [];
}

/**
 * Cache SGW Approvals to localStorage for the given company.
 */
export function saveStoredSgwApprovals(approvals, companyId) {
  try {
    localStorage.setItem(sgwApprovalsStorageKey(companyId), JSON.stringify(approvals || []));
  } catch (e) {
    console.error("Error saving SGW approvals to localStorage:", e);
  }
}
